<template>
  <div :class="['mb-2', { 'opacity-50': saving }]">
    <div class="mb-2">
      <Text size="xl" bold> REDCap Project Details </Text>
    </div>
    <div class="card w-most p-2 mb-2 flex justify-between">
      <Text size="lg" italic>Title</Text>
      <Text size="lg">{{ project.name }} </Text>
    </div>
    <div class="card w-most p-2 mb-2 flex justify-between">
      <div class="flex justify-left">
        <Text size="lg" italic>Production Mode</Text>
        <InfoIcon
          class="normal-case pl-2 pt-1"
          :info="'When Production Mode is activated in the synced REDCap project, this site can no longer modify the REDCap project.'"
        />
      </div> 
      <Text size="lg">{{ redcapProjectInfo?.in_production ? 'Activated' : 'Not Activated' }}</Text>
    </div>
    <div class="pt-2 pb-4">
      <Button
        :disabled="unsyncing"
        theme="primary"
        :showSpinner="unsyncing"
        size="xs"
        @click="unsyncTokenFromUser"
      >
      Unsync User from REDCap Project 
      </Button>
      <Text :class="{ hidden: !error }">{{ unsyncErrorMessage }}</Text>
    </div>
    <div class="p-2">
      <Text size="xl" bold> OTLFB Survey Details </Text>
    </div>
    <div class="card w-most p-2 mb-2 flex justify-between">
      <Text size="lg" italic>Week length</Text>
      <Text>
        {{ props.otlfbSurvey.dayCount / 7 }} Weeks ({{
          props.otlfbSurvey.dayCount
        }}
        Days)</Text
      >
    </div>
    <div class="card w-most p-2 mb-2 flex justify-between">
      <div class="flex items-center">
        <Text size="lg" italic> {{ substancesList.length === 1 ? "TLFB Variable" : "TLFB Variables" }} </Text>
        <InfoIcon
          v-if="instructionUrlInfoText !== ''"
          class="normal-case pl-2"
          :info=instructionUrlInfoText
        />
      </div>
      <div class="flex flex-col">
        <div v-for="(s, i) in substancesList" :key="s">
          <Text v-if="!instructionUrls[i]" class="p-1">
            {{ i + 1 }}. {{ s }}{{ hasPresetInstructions(otlfbSurvey.otlfbSurveySubstances[i], otlfbSurvey.otlfbSurveySubstances[i].substanceMode.dataTyp) ? '*' : '' }}
          </Text>
          <div v-else class="p-1">
            <a
              :href="instructionUrls[i]"
              target="_blank"
            >
              {{ i + 1 }}. {{ s }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="card w-most p-2 mb-2 flex justify-between">
      <div class="flex items-center">
        <Text size="lg" italic>Test Link</Text>
        <InfoIcon
          v-if="testSurvey!==''"
          class="normal-case pl-2"
          :info="'Suggestion: After testing survey, delete test record.'"
        />
      </div>
      <div class="flex flex-col items-center">
        <div v-if="testSurvey===''" class="pt-1">
          <Button
            :disabled="saving || !!redcapProjectInfo?.in_production"
            theme="secondary"
            size="md"
            @click="showGenerateLinkModal = true"
          >
            Generate Test Link
          </Button>
        </div>
        <div class="pt-1">
          <Button
              v-if="showDeleteTestRecordButton"
              theme="primary"
              size="sm"
              @click="clearTestData"
            >
              Delete Test Record
          </Button>
        </div>
        <div v-if="testSurvey !== ''" class="pt-2 pb-2">
          <a
            v-if="isDemo"
            href="#"
            @click.prevent="onDemoTestLinkClick"
          >
            {{ `Record ${testRecord} - Test Link` }}
          </a>
          <a
            v-else
            :href="testSurvey"
            target="_blank"
            @click="showDeleteTestRecordButton = true"
          >
            {{ `Record ${testRecord} - Test Link` }}
          </a>
        </div>
      </div> 
    </div>
  </div>
  <div>
    <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
  </div>
  <Button class="mt-4" theme="primary" @click="showDeleteSurveyModal = true">
    Delete Survey
  </Button>

  <Modal v-if="showDeleteSurveyModal">
    <div class="flex flex-col gap-4 p-2 w-88">
      <div class="text-center flex flex-col gap-1">
        <h1 class="m-0 text-center">Delete Survey</h1>
        <p class="m-0">
          Are you sure you want to delete this survey? This will delete all TLFB
          fields and data in REDCap.
        </p>
      </div>
      <div class="w-1/2 m-auto flex gap-2 mt-3">
        <Button :disabled="saving" @click="showDeleteSurveyModal = false" stretch> Cancel </Button>
        <Button
          :disabled="saving || !!redcapProjectInfo?.in_production"
          theme="primary"
          stretch
          :showSpinner="saving"
          @click="deleteSurvey"
        >
          Delete
        </Button>
        <div>
          <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
        </div>
      </div>
    </div>
  </Modal>
  <Modal v-if="showGenerateLinkModal">
    <div class="flex flex-col gap-4 p-2 w-112">
      <div class="text-center flex flex-col gap-1">
        <h1 class="m-0 text-center">Generate Test Link</h1>
        <p class="m-0 mt-4 text-left">
          {{ `Would you like to create a new record in REDCap for testing the TLFB survey?
          The record_id will be ${testRecord}, one more than the currently largest record_id.` }}
        </p>
      </div>
      <div class="w-1/2 m-auto flex gap-2 mt-2">
        <Button :disabled="saving" @click="showGenerateLinkModal = false" stretch>Cancel</Button>
        <Button
          :disabled="saving"
          theme="primary"
          stretch
          :showSpinner="saving"
          @click="generateNewRow"
        >
          Create
        </Button>
        <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
      </div>
    </div>
  </Modal>
  <Modal v-if="showDemoSurveyModal">
    <div class="flex flex-col gap-4 p-2 w-112">
      <div class="text-center flex flex-col gap-1">
        <h1 class="m-0 text-center">Open Demo Survey</h1>
        <p class="m-0 mt-4 text-left">
          Would you like to open a demo survey with variables: Alcohol (numeric),
          Cigarettes (numeric), and E-Cigarettes (binary)? This demo survey may not
          reflect the variables you selected in the demo admin page.
        </p>
      </div>
      <div class="w-most m-auto flex gap-2 mt-2">
        <Button @click="showDemoSurveyModal = false" stretch>Cancel</Button>
        <Button theme="primary" stretch @click="openDemoSurvey">
          Open Demo Survey
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script setup>
// import ApiClient from '../services/ApiClient'
import { computed, ref, onMounted, watch, inject } from "vue"
import ApiClient, { OtlfbSurveyApi, RedcapApiTokenApi } from "../api"
import Text from "../components/Text.vue"
import Button from "../components/Button.vue"
import Modal from "../components/Modal.vue"
import InfoIcon from "../components/InfoIcon.vue"
import toUpperCase from "../utils/toUpperCase.js"
import hasPresetInstructions from "../utils/hasPresetInstructions"
// const metadata = ref({ data: [] })

// const user = inject('user')
const emit = defineEmits(["survey-deleted", "project-unsynced-from-user"])

const props = defineProps({
  project: {
    type: Object,
    required: true
  },
  token: {
    type: [String, Object],
    required: true
  },
  otlfbSurvey: {
    type: Object,
    required: true
  },
  redcapProjectInfo: {
    type: Object,
    required: true
  }
})

const user = inject('user')
const isDemo = __DEMO_MODE__
const saving = ref(false)
const errorMessage = ref("")
const error = computed(() => errorMessage.value !== "")
const showDeleteSurveyModal = ref(false)
const showGenerateLinkModal = ref(false)
const showDemoSurveyModal = ref(false)
const showDeleteTestRecordButton = ref(false)
const testRecord = ref(null)
const testSurvey = ref('')

const DEMO_SURVEY_URL =
  `${(process.env.VUE_APP_SURVEY_SITE_URL || "https://flextlfb-demo.pages.dev").replace(/\/$/, "")}/demo/default-substances`

function onDemoTestLinkClick() {
  showDeleteTestRecordButton.value = true
  showDemoSurveyModal.value = true
}

function openDemoSurvey() {
  showDemoSurveyModal.value = false
  window.open(DEMO_SURVEY_URL, "_blank", "noopener,noreferrer")
}
const substancesList = computed(() => {
  return props.otlfbSurvey.otlfbSurveySubstances.map((oss, i) => {
    return oss.substanceMode.name === '' 
      ? `${toUpperCase(oss.substance.name)} - ${toUpperCase(oss.substanceMode.dataTyp)}`
      : `${toUpperCase(oss.substance.name)} (${toUpperCase(oss.substanceMode.name)}) - ${toUpperCase(oss.substanceMode.dataTyp)}`
  })
})

const instructionUrls = computed(() => {
  return props.otlfbSurvey.otlfbSurveySubstances.map((oss) => { return oss.instructionUrl })
})

onMounted(async () => {
  testRecord.value = await fetchNextRedcapRecord()
})

watch(() => props.project, async () => {
  testRecord.value = await fetchNextRedcapRecord()
  showGenerateLinkModal.value = false
  showDeleteTestRecordButton.value = false
  testSurvey.value = ''
})

async function deleteSurvey() {
  errorMessage.value = ""
  saving.value = true

  // if (!user.value.usableByApi) {
  //   errorMessage.value = 'This user cannot delete surveys, likely due to username not registered in REDCap project or user not granted API rights.'
  // } else {
  try {
    if (props.redcapProjectInfo.in_production === 0) {
      const response = await OtlfbSurveyApi.destroy(props.otlfbSurvey.id)
      if (response.status === 200) {
        emit("survey-deleted")
        showDeleteSurveyModal.value = false
      }
    } else {
      errorMessage.value = "REDCap forms cannot be deleted while project is in production."
    }
  } catch (error) {
    errorMessage.value = "Error deleting TLFB forms in REDCap. Please try again later."
  }

  saving.value = false
}

async function generateNewRow() {
  errorMessage.value = ""
  saving.value = true
  try {
    if (props.redcapProjectInfo.in_production === 0) {
      const response = await ApiClient.post("/import", {
        content: "newRecord",
        projectId: props.project.id,
      })
      if (response.status === 200) {
        showGenerateLinkModal.value = false
        fetchTestSurveyLink()
      }
    } else {
      errorMessage.value = "Records cannot be created when project is in production."
    }
  } catch (error) {
    errorMessage.value = "Error creating test record in REDCap. Please try again later."
  }
  saving.value = false
}

async function fetchTestSurveyLink() {
  try {
    const response = await ApiClient.post(`/export`, {
      content: "testLink",
      projectId: props.project.id,
    })
    if (response.status === 200) {
      let link = response.data?.[0].field_label.replace('[record_id]', testRecord.value)
      // Fix placeholder or relative links so test link opens the survey app
      const surveyBase = process.env.VUE_APP_SURVEY_SITE_URL || 'https://d25tj1ur33w4i3.cloudfront.net'
      if (!link.startsWith('http://') && !link.startsWith('https://')) {
        link = link.startsWith('CHANGE_ME_AFTER_FRONTEND_DEPLOYMENT')
          ? link.replace('CHANGE_ME_AFTER_FRONTEND_DEPLOYMENT', surveyBase)
          : `${surveyBase}/${link.replace(/^\//, '')}`
      } else if (link.startsWith('CHANGE_ME_AFTER_FRONTEND_DEPLOYMENT')) {
        link = link.replace('CHANGE_ME_AFTER_FRONTEND_DEPLOYMENT', surveyBase)
      }
      testSurvey.value = link
      showDeleteTestRecordButton.value = true
    }
  } catch (error) {
    testSurvey.value = ""
    showDeleteTestRecordButton.value = false
    errorMessage.value = "Test link could not be retrieved from REDCap. Please try again later."
  }
}

async function fetchNextRedcapRecord() {
  try {
    const response = await ApiClient.post(`/export`, {
      content: "nextRecord",
      projectId: props.project.id,
    })
    if (response.status === 200) {
      return response.data.nextRecord
    } else {
      console.error(`Unexpected response status: ${response.status}`)
      return null
    }
  } catch (error) {
    console.error('Error fetching next REDCap record:', error.response ? error.response.data : error.message)
    return null
  }
}

async function fetchRecordIds() {
  try {
    const response = await ApiClient.post(`/export`, {
      content: "recordIds",
      projectId: props.project.id,
    })
    if (response.status === 200) {
      return response.data.map((d) => { return d.record_id})
    }
  } catch (error) {
    return null
  }
}

async function clearTestData() {
  errorMessage.value = ''
  try {
    const response = await ApiClient.post("/import", {
      content: "destroySurveyData",
      projectId: props.project.id,
      recordId: testRecord.value,
    })
    if (response.status === 200) {
      showDeleteTestRecordButton.value = false
      testSurvey.value = ""
    }
  } catch (error) {
    errorMessage.value = `Failed to delete data for record ${testRecord.value}, likely because user does not have Delete Record rights in REDCap project.`
  }
}

async function submit() {
  showGenerateLinkModal.value = false
  await fetchTestSurveyLink()
}

const unsyncErrorMessage = ref('')
const unsyncing = ref(false)
async function unsyncTokenFromUser() {
  unsyncErrorMessage.value = ''
  unsyncing.value = true
  try {
    const tokenId = typeof props.token === 'string' ? null : props.token?.id
    if (!tokenId) {
      unsyncErrorMessage.value = "Error: Token ID not available"
      // Token may already have been removed; refresh so project disappears from dashboard
      emit('project-unsynced-from-user')
      return
    }
    const response = await RedcapApiTokenApi.destroy(tokenId)
    if (response?.status === 200) {
      emit('project-unsynced-from-user')
    }
  } catch (error) {
    console.log(error.message)
    unsyncErrorMessage.value = 'Error unsyncing token: ' + error.message
  } finally {
    unsyncing.value = false
  }
}

const includesSubstancesWithPresetInstructions = computed(() => {
  return props.otlfbSurvey.otlfbSurveySubstances.some(oss => hasPresetInstructions(oss, oss.substanceMode.dataTyp))
})

const instructionUrlInfoText = computed(() => {

  let instructions = ""
  if (instructionUrls.value.filter((url) => url).length !== 0) {
    instructions = instructionUrls.value.filter((url) => url).length > 1
    ? "Click hyperlinks to view reference guides."
    : "Click hyperlink to view reference guides."
  }
  
  return includesSubstancesWithPresetInstructions.value
    ? instructions + ' *Built-in Instructions'
    : instructions
})

</script>

<style scoped></style>
